// WARNING: Some of these functions are called client side

import forEach from 'lodash-es/forEach.js';
import groupBy from 'lodash-es/groupBy.js';
import { djs, type Dayjs } from '@duke-hq/djs';
import { resolveDurationWeeks } from '@duke-hq/lib/resolveDurationWeeks';
import {
  StudentResourceEnrolmentStatus,
  StudentCourseEnrolmentScheduleItem,
  StudentCourseEnrolmentStatus,
  type StudentCourseEnrolmentScheduleAdjustments,
} from '@duke-hq/svc-api-db/client';
import {
  CourseEnrolmentScheduleItemType,
  ScheduleWeekState,
} from '@duke-hq/svc-api/student/trpc/routers/types/ScheduleItemType';
import { AdminCourseScheduleItemType } from '@duke-hq/svc-api/student/trpc/routers/types/AdminCourseScheduleItemType';
import type { EnrichedCourseEnrolment } from '@duke-hq/svc-api/student/trpc/helpers/enrichedCourseEnrolment';
import { StudentScheduleItemType } from '@duke-hq/svc-api-db/schemas/enums/StudentScheduleItemType.schema';
import { getSemesterStartDate } from './getSemesterStartDate';

type CourseItem = NonNullable<
  NonNullable<EnrichedCourseEnrolment['course']['courseItems']>[number]
>;

export type StudentScheduleItem = {
  id: string;
  Week: string;
  type: StudentScheduleItemType;
  Title: string;
  unitId: string;
  Semester: string;
  'Unit Code': string;
};

function getCourseItem(
  scheduleItemId: string,
  scheduleItemType: string,
  courseEnrolment: EnrichedCourseEnrolment,
) {
  if (!courseEnrolment.course.courseItems) {
    return undefined;
  }

  switch (scheduleItemType) {
    case 'ASSESSMENT':
      return courseEnrolment.course.courseItems.find((courseItem) => {
        return courseItem.unitOfCompetency?.unitAssessment?.id === scheduleItemId;
      });
    case 'CONVERSATION':
      return courseEnrolment.course.courseItems.find((courseItem): boolean => {
        return (
          courseItem.unitOfCompetency?.learningResources?.some(
            ({ resource }) => resource.id === scheduleItemId,
          ) ?? false
        );
      });
    case 'UNIT':
      return courseEnrolment.course.courseItems.find(
        (courseItem) => courseItem.unitOfCompetency?.id === scheduleItemId,
      );
  }
}

function getCourseItemTitle(courseItem: CourseItem, itemType: string, itemId: string) {
  switch (itemType) {
    case 'ASSESSMENT':
      return `${courseItem.unitOfCompetency?.title} - Assessment`;
    case 'CONVERSATION':
      return (
        courseItem.unitOfCompetency?.learningResources.find(
          ({ resource }) => resource.id === itemId,
        )?.resource.title ?? courseItem.unitOfCompetency?.title
      );
    case 'UNIT':
    default:
      return courseItem.unitOfCompetency?.title;
  }
}

function getLastPublishedVersionId(
  courseItem: CourseItem,
  itemType: string,
  itemId: string,
): number | null | undefined {
  switch (itemType) {
    case 'CONVERSATION':
      return courseItem.unitOfCompetency?.learningResources.find(
        ({ resource }) => resource.id === itemId,
      )?.resource.last_published_version_id;
    case 'ASSESSMENT':
    case 'UNIT':
    default:
      return undefined;
  }
}

function getEstimatedMinutes(
  courseItem: CourseItem,
  itemType: string,
  itemId: string,
): number | null {
  switch (itemType) {
    case 'CONVERSATION':
      return (
        courseItem.unitOfCompetency?.learningResources.find(
          ({ resource }) => resource.id === itemId,
        )?.resource.estimatedCompletionMinutes ?? null
      );
    case 'ASSESSMENT':
      return courseItem.unitOfCompetency?.unitAssessment?.estimatedCompletionMinutes ?? null;
    case 'UNIT':
    default:
      return null;
  }
}

export interface ScheduleWeek {
  week: number;
  weekStatusRelativeToToday: ScheduleWeekState;
  hasItemsRequiringRevision: boolean;
  isUnitComplete: boolean;
  startOfWeek: Date;
  endOfWeek: Date;
  items: CourseEnrolmentScheduleItemType[];
}

export interface StudentCourseSchedule {
  weeks: ScheduleWeek[];
}

export interface FullCourseSchedule {
  lln?: StudentCourseSchedule;
  isUSIRequired: boolean;
  isUSIProvided: boolean;
  isLLNRequired: boolean;
  isLLNAwaitingAssessment: boolean;
  isLLNComplete: boolean;
  isSemesterOneComplete: boolean;
  semesters: {
    [key: string]: StudentCourseSchedule;
  };
}

// This is the minimum information required to adjust a schedule item
export type PartialCourseEnrolmentScheduleAdjustments = Pick<
  StudentCourseEnrolmentScheduleAdjustments,
  'item_id' | 'scheduled_start_date'
> &
  Partial<StudentCourseEnrolmentScheduleAdjustments>;

function applyScheduleAdjustment(
  schedule: AdminCourseScheduleItemType[],
  adminCourseScheduleItem: AdminCourseScheduleItemType,
  adjustedItem: PartialCourseEnrolmentScheduleAdjustments,
  lengthOfStudyWeek: number,
  notRequiredWeeks: number = 0,
) {
  const adjustedItemInSchedule = schedule.find((o) => o.id === adjustedItem.item_id);

  if (adjustedItemInSchedule) {
    const courseScheduleWeek = parseInt(adminCourseScheduleItem.Week);
    const courseScheduleWeekAdjustment = parseInt(adjustedItemInSchedule.Week);
    const semesterAdjustment =
      adjustedItemInSchedule.Semester === '1' && adminCourseScheduleItem.Semester === '2';

    // Adjust the week number based on semester logic
    const adjustedWeekNumMinusCurrentWeek = semesterAdjustment
      ? courseScheduleWeek + 13 - courseScheduleWeekAdjustment
      : Math.max(courseScheduleWeek - courseScheduleWeekAdjustment, 0);

    return djs(adjustedItem.scheduled_start_date)
      .startOf('day')
      .add(
        Math.max(adjustedWeekNumMinusCurrentWeek - notRequiredWeeks, 0) * lengthOfStudyWeek,
        'week',
      );
  }

  return null;
}

function getCourseStartDateForScheduleItems(
  semester: number,
  courseEnrolment: EnrichedCourseEnrolment,
) {
  const startDate = djs(getSemesterStartDate(semester, courseEnrolment));

  return semester === 0 ? startDate : startDate.startOf('day');
}

function getScheduleItemStartDate(
  courseEnrolment: EnrichedCourseEnrolment,
  adminCourseScheduleItem: AdminCourseScheduleItemType,
  adjustedItem?: PartialCourseEnrolmentScheduleAdjustments,
  notRequiredWeeks: number = 0,
): Dayjs {
  const lengthOfStudyWeek = courseEnrolment.work_schedule === 'PART_TIME' ? 2 : 1;
  const startOfFirstWeek = getCourseStartDateForScheduleItems(
    parseInt(adminCourseScheduleItem.Semester),
    courseEnrolment,
  );
  const weekNumMinusCurrentWeek =
    parseInt(adminCourseScheduleItem.Week) - 1 <= 0
      ? 0
      : parseInt(adminCourseScheduleItem.Week) - 1;

  if (
    adjustedItem &&
    Array.isArray(courseEnrolment.course.schedule) &&
    courseEnrolment.course.schedule.length > 0
  ) {
    const adjustment = applyScheduleAdjustment(
      courseEnrolment.course.schedule,
      adminCourseScheduleItem,
      adjustedItem,
      lengthOfStudyWeek,
      notRequiredWeeks,
    );
    if (adjustment) {
      return adjustment;
    }
  }

  return startOfFirstWeek.add(
    (weekNumMinusCurrentWeek - notRequiredWeeks) * lengthOfStudyWeek,
    'week',
  );
}

// The due date is the end of the item's study window: its release week plus its own duration in
// study weeks. Without a duration this defaults to a single study week, which is the legacy
// behaviour.
function getScheduleItemDueDate(
  courseEnrolment: EnrichedCourseEnrolment,
  adminCourseScheduleItem: AdminCourseScheduleItemType,
  adjustedItem?: PartialCourseEnrolmentScheduleAdjustments,
  notRequiredWeeks: number = 0,
  durationWeeks?: string | number | null,
): Dayjs {
  const courseStartDate = getCourseStartDateForScheduleItems(
    parseInt(adminCourseScheduleItem.Semester),
    courseEnrolment,
  );
  const lengthOfStudyWeek = courseEnrolment.work_schedule === 'PART_TIME' ? 2 : 1;
  const spanWeeks = resolveDurationWeeks(durationWeeks);
  const weekNumMinusCurrentWeek =
    parseInt(adminCourseScheduleItem.Week) - 1 <= 0
      ? 0
      : parseInt(adminCourseScheduleItem.Week) - 1;

  if (adjustedItem) {
    const adjustment = applyScheduleAdjustment(
      courseEnrolment.course.schedule,
      adminCourseScheduleItem,
      adjustedItem,
      lengthOfStudyWeek,
      notRequiredWeeks,
    );
    if (adjustment) {
      return adjustment.add(spanWeeks * lengthOfStudyWeek, 'week');
    }
  }

  return courseStartDate.add(
    (weekNumMinusCurrentWeek - notRequiredWeeks + spanWeeks) * lengthOfStudyWeek,
    'week',
  );
}

// Function to generate an empty {numOfWeeks}-week schedule
// Please note:
//
// Full time is:
//    13 weeks in Semester 1
//    13 weeks in Semester 2
//    (Week 1 - refers to a period of 1 week)
//
// Part time:
//    26 weeks in Semester 1
//    26 weeks in Semester 2
//    (Week 1 - refers to a period of 2 weeks)
export function generateEmptyStudentCourseSchedule(
  numOfWeeks: number = 13,
  scheduledItems: StudentCourseEnrolmentScheduleItem[],
  semester: string,
): StudentCourseSchedule {
  let weeks: ScheduleWeek[] = [];

  for (let i = 0; i < numOfWeeks; i++) {
    const scheduledStartOfTheWeek = scheduledItems.find(
      (scheduledItem) =>
        scheduledItem.admin_course_schedule_week === i + 1 &&
        scheduledItem.semester === Number(semester),
    );

    if (scheduledStartOfTheWeek) {
      weeks.push({
        week: i + 1,
        weekStatusRelativeToToday: ScheduleWeekState.FUTURE,
        hasItemsRequiringRevision: false,
        isUnitComplete: false,
        startOfWeek: scheduledStartOfTheWeek.scheduled_start_date,
        endOfWeek: scheduledStartOfTheWeek.scheduled_due_date,
        items: [],
      });
    }
  }

  weeks = calculateWeekStatusRelativeToToday(weeks);
  return { weeks };
}

function calculateWeekStatusRelativeToToday(weeks: ScheduleWeek[]): ScheduleWeek[] {
  const today = djs();

  const currentSemesterWeekIndex = weeks.findIndex((week) => {
    return today.isBetween(week.startOfWeek, week.endOfWeek, 'day', '[]');
  });

  return weeks.map((week, index) => {
    if (currentSemesterWeekIndex !== -1) {
      if (index <= currentSemesterWeekIndex - 2) {
        return {
          ...week,
          weekStatusRelativeToToday: ScheduleWeekState.AT_RISK,
        };
      } else if (index < currentSemesterWeekIndex) {
        return {
          ...week,
          weekStatusRelativeToToday: ScheduleWeekState.OVERDUE,
        };
      } else if (index === currentSemesterWeekIndex) {
        return {
          ...week,
          weekStatusRelativeToToday: ScheduleWeekState.THIS_WEEK,
        };
      } else {
        return { ...week, weekStatusRelativeToToday: ScheduleWeekState.FUTURE };
      }
    } else {
      // If today is not within any semester weeks, update statuses based on dates
      // LLN 1 week semester will often fall into this category
      if (today.isBefore(week.startOfWeek)) {
        return { ...week, weekStatusRelativeToToday: ScheduleWeekState.FUTURE };
      } else if (today.isAfter(week.endOfWeek)) {
        return {
          ...week,
          weekStatusRelativeToToday: ScheduleWeekState.AT_RISK,
        };
      } else {
        return week;
      }
    }
  });
}

function getProgress(
  scheduleItemUnitType: string,
  resourceEnrolment?: { progress_percent: number },
  unitAssessmentEnrolment?: { progress_percent: number },
): number {
  let progress = 0;

  switch (scheduleItemUnitType) {
    case 'ASSESSMENT':
      progress = unitAssessmentEnrolment ? unitAssessmentEnrolment.progress_percent : 0;
      break;
    case 'CONVERSATION':
      progress = resourceEnrolment?.progress_percent ?? 0;
      break;
    case 'UNIT':
      progress = 0;
      break;
  }

  return progress;
}

function isSemesterOneCompleted(scheduleItems: CourseEnrolmentScheduleItemType[]): boolean {
  return scheduleItems
    .filter((scheduleItem) => scheduleItem.semester === 1 && scheduleItem.itemType !== 'UNIT')
    .every((scheduleItem: CourseEnrolmentScheduleItemType) => {
      return (
        scheduleItem.isComplete ||
        (scheduleItem.itemType === 'ASSESSMENT' &&
          (scheduleItem.assessmentEnrolmentStatus === StudentResourceEnrolmentStatus.COMPLETED ||
            scheduleItem.assessmentEnrolmentStatus ===
              StudentResourceEnrolmentStatus.CREDIT_TRANSFER ||
            scheduleItem.assessmentEnrolmentStatus ===
              StudentResourceEnrolmentStatus.RECOGNITION_OF_PRIOR_LEARNING ||
            scheduleItem.assessmentEnrolmentStatus === StudentResourceEnrolmentStatus.NOT_REQUIRED))
      );
    });
}

function isCourseItemAbleToBeStarted(
  scheduleItems: CourseEnrolmentScheduleItemType[],
  courseEnrolmentScheduleItem: CourseEnrolmentScheduleItemType,
): boolean {
  const today = djs().endOf('day');
  const itemStartDate = djs(courseEnrolmentScheduleItem.startDate).endOf('day');

  if (itemStartDate.isAfter(today)) {
    return false;
  }

  const itemIndex = scheduleItems.findIndex(
    (scheduleItem) => scheduleItem.id === courseEnrolmentScheduleItem.id,
  );
  const isFirstItemInSchedule = itemIndex === 0;

  const allPreviousItemsAreComplete = scheduleItems
    .slice(0, itemIndex)
    .every((scheduleItem: CourseEnrolmentScheduleItemType) => {
      return (
        scheduleItem.isComplete ||
        scheduleItem.itemType === 'UNIT' ||
        (scheduleItem.itemType === 'ASSESSMENT' &&
          (scheduleItem.assessmentEnrolmentStatus === StudentResourceEnrolmentStatus.SUBMITTED ||
            scheduleItem.assessmentEnrolmentStatus === StudentResourceEnrolmentStatus.REVISION ||
            scheduleItem.assessmentEnrolmentStatus ===
              StudentResourceEnrolmentStatus.CREDIT_TRANSFER ||
            (scheduleItem.assessmentEnrolmentStatus ===
              StudentResourceEnrolmentStatus.IN_PROGRESS &&
              scheduleItem.firstSubmittedAt !== null) ||
            scheduleItem.assessmentEnrolmentStatus ===
              StudentResourceEnrolmentStatus.RECOGNITION_OF_PRIOR_LEARNING ||
            scheduleItem.assessmentEnrolmentStatus === StudentResourceEnrolmentStatus.NOT_REQUIRED))
      );
    });

  return isFirstItemInSchedule || allPreviousItemsAreComplete;
}

function disableAllItemsInSemester(studentCourseSchedule: StudentCourseSchedule) {
  return {
    ...studentCourseSchedule,
    weeks: studentCourseSchedule.weeks.map((week) => ({
      ...week,
      items: week.items.map((item) => ({
        ...item,
        disabled: true,
        isCourseItemAbleToBeStarted: false,
      })),
    })),
  };
}

function disableAllItems(semesters: { [key: string]: StudentCourseSchedule }) {
  return Object.fromEntries(
    Object.entries(semesters).map(([key, studentCourseSchedule]) => [
      key,
      disableAllItemsInSemester(studentCourseSchedule),
    ]),
  );
}

function getAllSemesterItems(studentCourseSchedule: StudentCourseSchedule) {
  return studentCourseSchedule.weeks
    .map((week) => week.items)
    .flat()
    .sort((a, b) => a.position - b.position);
}

function getAllScheduleItems(
  courseSchedule: FullCourseSchedule,
): CourseEnrolmentScheduleItemType[] {
  const allItems: CourseEnrolmentScheduleItemType[] = [];
  const allSemestersIncludingLLN: StudentCourseSchedule[] = [
    ...(courseSchedule.lln ? [courseSchedule.lln] : []),
    ...Object.values(courseSchedule.semesters),
  ];

  for (const studentCourseSchedule of Object.values(allSemestersIncludingLLN)) {
    allItems.push(...getAllSemesterItems(studentCourseSchedule).flat());
  }

  return allItems.sort((a, b) => a.position - b.position);
}

function getNextCourseItemAbleToBeStarted(courseSchedule: FullCourseSchedule) {
  const allScheduleItems = getAllScheduleItems(courseSchedule);
  return allScheduleItems.find(
    (scheduleItem) =>
      scheduleItem.itemType != 'UNIT' &&
      !scheduleItem.isComplete &&
      isCourseItemAbleToBeStarted(allScheduleItems, scheduleItem),
  );
}

function getNextCourseItemAfterItemId(
  courseSchedule: FullCourseSchedule,
  itemId: string | undefined,
): CourseEnrolmentScheduleItemType | undefined {
  if (itemId == null) {
    return undefined;
  }
  const allScheduleItems = getAllScheduleItems(courseSchedule);
  const courseItemIndex = allScheduleItems.findIndex(
    (scheduleItem) => scheduleItem.itemId === itemId,
  );

  return courseItemIndex > 0 && allScheduleItems.length >= courseItemIndex + 1
    ? allScheduleItems[courseItemIndex + 1]
    : undefined;
}

function getUnitAssessmentLastPublishedVersionId(unitOfCompetency: unknown): string | undefined {
  if (
    typeof unitOfCompetency === 'object' &&
    unitOfCompetency !== null &&
    'unitAssessment' in unitOfCompetency
  ) {
    const unitAssessment = (unitOfCompetency as Record<string, unknown>).unitAssessment;

    if (
      typeof unitAssessment === 'object' &&
      unitAssessment !== null &&
      'lastPublishedVersionId' in unitAssessment
    ) {
      const versionId = (unitAssessment as Record<string, unknown>).lastPublishedVersionId;
      return typeof versionId === 'string' ? versionId : undefined;
    }
  }

  return undefined;
}

function decorateScheduleItems({
  scheduleItems,
  courseEnrolment,
}: {
  scheduleItems: StudentCourseEnrolmentScheduleItem[];
  courseEnrolment: EnrichedCourseEnrolment;
}): CourseEnrolmentScheduleItemType[] {
  const decoratedScheduleItems: CourseEnrolmentScheduleItemType[] = [];
  const { resourceEnrolments, unitAssessmentEnrolments } = courseEnrolment;

  forEach(scheduleItems, (scheduleItem) => {
    const courseItem = getCourseItem(scheduleItem.item_id, scheduleItem.item_type, courseEnrolment);
    const unitOfCompetency = courseItem?.unitOfCompetency;

    if (!unitOfCompetency) {
      // We are currently lacking referential integrity between the course and course.schedule is Admin
      // and ultimately the student schedule too :(
      // When an item is removed from a course, it will still exist in the student schedule so we need
      // to exclude these items from the decoratedScheduleItems that gets returned by this function
      return;
    }

    const unitAssessmentEnrolment =
      unitOfCompetency?.unitAssessment?.id != null
        ? unitAssessmentEnrolments?.find(
            ({ unit_assessment_id }) => unit_assessment_id === unitOfCompetency?.unitAssessment?.id,
          )
        : undefined;

    const resourceEnrolment = resourceEnrolments?.find(
      ({ resource_id }) => resource_id === scheduleItem.item_id,
    );

    decoratedScheduleItems.push({
      id: scheduleItem.id,
      courseEnrolmentId: scheduleItem.course_enrolment_id,
      isComplete: scheduleItem.is_complete,
      startedAt: scheduleItem.started_at || undefined,
      semester: scheduleItem.semester,
      adminCourseScheduleWeek: scheduleItem.admin_course_schedule_week || undefined,
      position: scheduleItem.position,
      title:
        getCourseItemTitle(courseItem, scheduleItem.item_type, scheduleItem.item_id) ?? 'Unknown',
      unitOfCompetencyId: scheduleItem.unit_of_competency_id,
      unitOfCompetencyCode: unitOfCompetency?.code ?? 'Unknown',
      itemId: scheduleItem.item_id,
      itemType: scheduleItem.item_type,
      assessmentEnrolmentId: unitAssessmentEnrolment?.id, // TODO - this will only be set for assessments
      assessmentEnrolmentStatus: unitAssessmentEnrolment?.status, // TODO - this will only be set for assessments
      firstSubmittedAt: unitAssessmentEnrolment?.submission_first_submitted_at ?? null,
      resourceLastPublishedVersionId:
        getLastPublishedVersionId(courseItem, scheduleItem.item_type, scheduleItem.item_id) ??
        undefined, // TODO - this will only be set for conversations
      resourceEnrolmentId: resourceEnrolment?.id, // TODO - this will only be set for conversations
      resourceEnrolmentStatus: resourceEnrolment?.status, // TODO - this will only be set for conversations
      progress: getProgress(scheduleItem.item_type, resourceEnrolment, unitAssessmentEnrolment),
      isCourseItemAbleToBeStarted: false, // We set this properly below
      disabled: false, // We set this properly below
      startDate: scheduleItem.scheduled_start_date,
      dueDate: scheduleItem.scheduled_due_date,
      unitAssessmentLastPublishedVersionId:
        getUnitAssessmentLastPublishedVersionId(unitOfCompetency),
      estimatedCompletionMinutes: getEstimatedMinutes(
        courseItem,
        scheduleItem.item_type,
        scheduleItem.item_id,
      ),
    });
  });

  const isEnrolmentWithdrawn = courseEnrolment.status === StudentCourseEnrolmentStatus.WITHDRAWN;

  decoratedScheduleItems.forEach((scheduleItem) => {
    if (isEnrolmentWithdrawn) {
      scheduleItem.isCourseItemAbleToBeStarted = false;
      scheduleItem.disabled = !scheduleItem.isComplete;
    } else {
      scheduleItem.isCourseItemAbleToBeStarted = isCourseItemAbleToBeStarted(
        decoratedScheduleItems,
        scheduleItem,
      );
      scheduleItem.disabled = !scheduleItem.isCourseItemAbleToBeStarted;
    }
  });

  return decoratedScheduleItems;
}

function sortScheduleItemsIntoWeeks(
  schedule: StudentCourseSchedule,
  items: CourseEnrolmentScheduleItemType[],
): StudentCourseSchedule {
  const updatedWeeks = schedule.weeks.map((week) => {
    // Temp solution while we migrate data to a single format
    // - If the semester starts from week 1, we need to adjust the week number for semester 2
    const isSemesterTwoStartsFromOne = items
      .filter((item) => item.semester === 2)
      .every(
        (item) =>
          typeof item.adminCourseScheduleWeek === 'number' &&
          item.adminCourseScheduleWeek >= 1 &&
          item.adminCourseScheduleWeek <= 13,
      );

    const itemsForWeek = items.filter((item) => {
      if (item.adminCourseScheduleWeek == null) {
        return false;
      }
      const semesterOneWeeks = 13;
      return item.semester === 2 && !isSemesterTwoStartsFromOne
        ? item.adminCourseScheduleWeek - semesterOneWeeks === week.week
        : item.adminCourseScheduleWeek === week.week;
    });

    const newItems = [...week.items, ...itemsForWeek];

    return {
      ...week,
      items: newItems.sort((a, b) => a.position - b.position),
      hasItemsRequiringRevision: newItems.some(
        (scheduleItem) =>
          scheduleItem.itemType === 'ASSESSMENT' &&
          scheduleItem.assessmentEnrolmentStatus === StudentResourceEnrolmentStatus.REVISION,
      ),
      isUnitComplete: newItems.every((item) => item.itemType === 'UNIT' || item.isComplete),
    };
  });

  return {
    ...schedule,
    weeks: updatedWeeks,
  };
}

function isLLNRequired(
  courseEnrolment: EnrichedCourseEnrolment,
  lln: StudentCourseSchedule | undefined,
): boolean {
  if (!lln || !courseEnrolment.course.accredited) {
    return false;
  }
  return getAllSemesterItems(lln).length > 0;
}

function isUSIRequired(courseEnrolment: EnrichedCourseEnrolment): boolean {
  return courseEnrolment.course.accredited;
}

function isLLNComplete(lln: StudentCourseSchedule | undefined): boolean {
  if (!lln || getAllSemesterItems(lln).length === 0) {
    return false;
  }

  return getAllSemesterItems(lln).every(
    (llnItem) => llnItem.isComplete || llnItem.itemType === 'UNIT',
  );
}

function isLLNAwaitingAssessment(lln: StudentCourseSchedule | undefined): boolean {
  if (!lln || getAllSemesterItems(lln).length === 0) {
    return false;
  }

  return getAllSemesterItems(lln).some(
    (llnItem) =>
      llnItem.itemType === 'ASSESSMENT' &&
      (llnItem.assessmentEnrolmentStatus === StudentResourceEnrolmentStatus.SUBMITTED ||
        llnItem.assessmentEnrolmentStatus === StudentResourceEnrolmentStatus.REVISION ||
        (llnItem.assessmentEnrolmentStatus === StudentResourceEnrolmentStatus.IN_PROGRESS &&
          llnItem.firstSubmittedAt !== null)),
  );
}

export function getFullCourseSchedule(
  courseEnrolment: EnrichedCourseEnrolment,
  isUSIProvided: boolean,
): FullCourseSchedule {
  const decoratedScheduleItems = decorateScheduleItems({
    scheduleItems: courseEnrolment.courseEnrolmentScheduleItems,
    courseEnrolment,
  });

  const groupedBySemester = groupBy(decoratedScheduleItems, 'semester');
  let semesters: Record<string, StudentCourseSchedule> = {};
  let lln: StudentCourseSchedule | undefined;

  const finalScheduleWeek = Math.max(
    ...decoratedScheduleItems
      .map((item) => item.adminCourseScheduleWeek)
      .filter((week): week is number => typeof week === 'number'),
  );

  for (const [semesterNum, semesterDetails] of Object.entries(groupedBySemester)) {
    if (semesterNum === '0') {
      // Treating the LLN as a 1 week semester to help with rendering for now - will replace this later
      // when we make the LLN a first class citizen
      const emptySemesterSchedule = generateEmptyStudentCourseSchedule(
        1,
        courseEnrolment.courseEnrolmentScheduleItems,
        semesterNum,
      );
      lln = sortScheduleItemsIntoWeeks(emptySemesterSchedule, semesterDetails);
    } else {
      const emptySemesterSchedule = generateEmptyStudentCourseSchedule(
        finalScheduleWeek || 13,
        courseEnrolment.courseEnrolmentScheduleItems,
        semesterNum,
      );
      semesters[semesterNum] = sortScheduleItemsIntoWeeks(emptySemesterSchedule, semesterDetails);
    }
  }

  if (
    (isUSIRequired(courseEnrolment) && !isUSIProvided) ||
    (isLLNRequired(courseEnrolment, lln) && !isLLNComplete(lln) && !isLLNAwaitingAssessment(lln)) ||
    !!courseEnrolment.deleted_at
  ) {
    semesters = disableAllItems(semesters);
  }

  const isSemesterOneComplete = isSemesterOneCompleted(decoratedScheduleItems);

  if (semesters['2'] !== undefined && semesters['2'].weeks?.length > 0 && !isSemesterOneComplete) {
    semesters['2'] = disableAllItemsInSemester(semesters['2']);
  }

  return {
    isUSIRequired: isUSIRequired(courseEnrolment),
    isUSIProvided,
    isLLNRequired: isLLNRequired(courseEnrolment, lln),
    isLLNAwaitingAssessment: isLLNAwaitingAssessment(lln),
    isLLNComplete: isLLNComplete(lln),
    isSemesterOneComplete,
    lln,
    semesters,
  };
}

export const scheduleService = {
  getFullCourseSchedule,
  getNextCourseItemAbleToBeStarted,
  getNextCourseItemAfterItemId,
  getScheduleItemDueDate,
  getScheduleItemStartDate,
  getSemesterStartDate,
};
